<template>
  <div class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-blueGray-200">
    <div class="rounded-t mb-0 px-4 py-3 border-0 bg-teal-400">
      <div class="flex flex-wrap items-center">
        <div class="relative w-full px-4 max-w-full flex-grow flex-1">
          <h3 class="font-semibold text-lg text-black">
            Payroll Sheets
          </h3>
        </div>
      </div>

      <div class="flex flex-wrap items-center mt-3">
        <div class="relative w-full px-4 max-w-full flex-grow flex-1 payroll-filters">
          <select
            name="filter_month"
            id="filter_month"
            v-model="f_month"
            @change="apply_filter"
          >
            <option v-for="month in months" :key="month" :value="month">
              {{ month }}
            </option>
          </select>

          <select
            name="filter_year"
            id="filter_year"
            v-model="f_year"
            @change="apply_filter"
          >
            <option v-for="year in years" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Desktop / tablet table -->
    <div class="block w-full overflow-x-auto payroll-table-wrap hidden md:block">
      <table class="items-center w-full bg-transparent border-collapse">
        <thead>
          <tr>
            <th
              class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-600"
            >
              Payroll Start
            </th>
            <th
              class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-600"
            >
              Payroll End
            </th>
            <th
              class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-600"
            >
              Payroll File
            </th>
            <th
              class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center bg-blueGray-600"
            >
              Time Created
            </th>
            <th
              class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center bg-blueGray-600"
            >
              Action
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="payroll in payroll_data"
            :key="payroll.id"
            class="border-b border-gray-300"
          >
            <td class="px-6 align-middle py-4 text-sm whitespace-nowrap font-semibold text-left">
              {{ payroll.payroll_start }}
            </td>

            <td class="px-6 align-middle py-4 text-sm whitespace-nowrap font-semibold text-left">
              {{ payroll.payroll_end }}
            </td>

            <td class="px-6 align-middle py-4 text-sm font-semibold text-left">
              <div
                v-for="file in payroll.payroll_file_content"
                :key="file"
                class="payroll-file-name"
              >
                {{ extractFileName(file) }}
              </div>
            </td>

            <td class="px-6 align-middle py-4 text-sm whitespace-nowrap font-semibold text-left">
              {{ formatCreatedAt(payroll.created_at) }}
            </td>

            <td class="px-6 align-middle py-4 text-sm font-semibold text-left">
              <div
                v-for="file in payroll.payroll_file_content"
                :key="`${payroll.id}-${file}`"
                class="payroll-action-row"
              >
                <button
                  class="bg-emerald-500 text-white text-xs font-bold uppercase px-4 py-2 rounded shadow outline-none focus:outline-none mr-2 mb-2 ease-linear transition-all duration-150"
                  @click="openFilePreview(file)"
                >
                  View
                </button>

                <button
                  class="bg-lightBlue-600 text-white text-xs font-bold uppercase px-4 py-2 rounded shadow outline-none focus:outline-none mb-2 ease-linear transition-all duration-150"
                  @click="downloadSingleFile(file)"
                >
                  Download
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="payroll_data.length === 0">
            <td colspan="5" class="px-6 py-8 text-center text-sm font-semibold text-blueGray-500">
              No payroll sheets found for this period.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile cards -->
    <div class="md:hidden px-4 py-4">
      <div
        v-for="payroll in payroll_data"
        :key="`mobile-${payroll.id}`"
        class="mobile-payroll-card"
      >
        <div class="mobile-payroll-row">
          <span class="mobile-payroll-label">Payroll Start</span>
          <span class="mobile-payroll-value">{{ payroll.payroll_start }}</span>
        </div>

        <div class="mobile-payroll-row">
          <span class="mobile-payroll-label">Payroll End</span>
          <span class="mobile-payroll-value">{{ payroll.payroll_end }}</span>
        </div>

        <div class="mobile-payroll-row">
          <span class="mobile-payroll-label">Time Created</span>
          <span class="mobile-payroll-value">{{ formatCreatedAt(payroll.created_at) }}</span>
        </div>

        <div class="mobile-payroll-files">
          <div
            v-for="file in payroll.payroll_file_content"
            :key="`mobile-file-${payroll.id}-${file}`"
            class="mobile-payroll-file-block"
          >
            <div class="mobile-file-name">
              {{ extractFileName(file) }}
            </div>

            <div class="mobile-file-actions">
              <button
                class="bg-emerald-500 text-white text-xs font-bold uppercase px-4 py-2 rounded shadow outline-none focus:outline-none ease-linear transition-all duration-150"
                @click="openFilePreview(file)"
              >
                View
              </button>

              <button
                class="bg-lightBlue-600 text-white text-xs font-bold uppercase px-4 py-2 rounded shadow outline-none focus:outline-none ease-linear transition-all duration-150"
                @click="downloadSingleFile(file)"
              >
                Download
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="payroll_data.length === 0"
        class="mobile-payroll-card text-center text-sm font-semibold text-blueGray-500"
      >
        No payroll sheets found for this period.
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  data() {
    const currentYear = new Date().getFullYear();
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const years = [];
    for (let i = currentYear - 5; i < currentYear + 5; i++) {
      years.push(i);
    }

    return {
      payroll: [],
      payroll_data: [],
      years: years,
      f_year: currentYear,
      f_month: months[new Date().getMonth()],
      months: months,
    };
  },

  methods: {
    sortPayrollLatest(data) {
      return [...data].sort((a, b) => {
        const aDate = new Date(a.payroll_start || a.created_at || 0);
        const bDate = new Date(b.payroll_start || b.created_at || 0);
        return bDate - aDate;
      });
    },

    async fetch_payroll() {
      try {
        const token = localStorage.getItem("token");
        const data_toke = token.split("$");
        const form_data = {
          client_id: data_toke[1],
        };

        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch_payroll_api`,
          form_data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const fetchedPayroll = this.sortPayrollLatest(response.data.data || []);
        this.payroll = fetchedPayroll;

        if (fetchedPayroll.length > 0) {
          const latestStart = new Date(fetchedPayroll[0].payroll_start);
          this.f_year = latestStart.getFullYear();
          this.f_month = this.months[latestStart.getMonth()];
        }

        this.apply_filter();
      } catch (error) {
        console.error("Backend error:", error);
      }
    },

    formatCreatedAt(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },

    extractFileName(filePath) {
      const rawName = filePath.substring(filePath.lastIndexOf("/") + 1);
      const decodedName = decodeURIComponent(rawName);

      let fileName = decodedName;
      let extension = "";

      if (fileName.includes(".")) {
        extension = fileName.substring(fileName.lastIndexOf("."));
        fileName = fileName.substring(0, fileName.lastIndexOf("."));
      }

      return fileName.length > 28
        ? fileName.substring(0, 28) + "..." + extension
        : fileName + extension;
    },

    apply_filter() {
      const filtered = this.payroll.filter((payroll) => {
        const payrollDate = new Date(payroll.payroll_start);
        const payrollYear = payrollDate.getFullYear();
        const payrollMonthName = this.months[payrollDate.getMonth()];

        return payrollYear == this.f_year && payrollMonthName === this.f_month;
      });

      this.payroll_data = this.sortPayrollLatest(filtered);
    },

    isPdf(fileUrl) {
      return fileUrl.toLowerCase().includes(".pdf");
    },

    isImage(fileUrl) {
      return /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(fileUrl);
    },

    openFilePreview(fileUrl) {
      const safeUrl = String(fileUrl).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
      const fileName = this.extractFileName(fileUrl);

      let html = "";

      if (this.isPdf(fileUrl)) {
        html = `
          <iframe
            src="${safeUrl}"
            style="width:100%; height:70vh; border:0; border-radius:8px;"
            title="Payroll Preview"
          ></iframe>
        `;
      } else if (this.isImage(fileUrl)) {
        html = `
          <img
            src="${safeUrl}"
            alt="${fileName}"
            style="max-width:100%; max-height:70vh; display:block; margin:0 auto; border-radius:8px;"
          />
        `;
      } else {
        html = `
          <div style="padding: 1rem; text-align:left;">
            <p style="margin-bottom: 1rem;">
              This file type cannot be previewed in the browser.
            </p>
            <a
              href="${safeUrl}"
              target="_blank"
              rel="noopener noreferrer"
              style="display:inline-block; background:#3b82f6; color:#fff; padding:10px 16px; border-radius:8px; font-weight:700; text-decoration:none;"
            >
              Download file
            </a>
          </div>
        `;
      }

      Swal.fire({
        title: fileName,
        html: html,
        width: "90%",
        showCloseButton: true,
        showConfirmButton: false,
      });
    },

    async downloadSingleFile(aws_url) {
      Swal.fire({
        title: "Downloading file",
        text: "Please wait while the file is being downloaded...",
        showCancelButton: false,
        showConfirmButton: false,
        allowOutsideClick: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await fetch(aws_url);
        const blob = await response.blob();

        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        const fileName = decodeURIComponent(
          aws_url.substring(aws_url.lastIndexOf("/") + 1)
        );
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        Swal.fire({
          icon: "success",
          title: "Download completed",
          showConfirmButton: false,
          timer: 1400,
        });
      } catch (error) {
        console.error("Error downloading file:", error);
        Swal.fire({
          icon: "error",
          title: "Download error",
          text: "An error occurred while downloading the file.",
          showConfirmButton: false,
          timer: 1800,
        });
      }
    },
  },

  mounted() {
    this.fetch_payroll();
  },
};
</script>

<style scoped>
.payroll-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.payroll-filters select {
  min-width: 160px;
  padding: 0.7rem 0.9rem;
  border-radius: 0.5rem;
  border: 1px solid #94a3b8;
  background: white;
}

.payroll-file-name {
  margin-bottom: 0.45rem;
  word-break: break-word;
}

.payroll-action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.mobile-payroll-card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  padding: 1rem;
  margin-bottom: 1rem;
}

.mobile-payroll-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;
}

.mobile-payroll-label {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 0.2rem;
}

.mobile-payroll-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  word-break: break-word;
}

.mobile-payroll-files {
  margin-top: 1rem;
}

.mobile-payroll-file-block {
  border-top: 1px solid #e2e8f0;
  padding-top: 0.85rem;
  margin-top: 0.85rem;
}

.mobile-file-name {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.75rem;
  word-break: break-word;
}

.mobile-file-actions {
  display: flex;
  gap: 0.6rem;
}

.mobile-file-actions button {
  flex: 1;
}

@media (max-width: 767px) {
  .payroll-filters {
    flex-direction: column;
  }

  .payroll-filters select {
    width: 100%;
    min-width: 0;
  }
}
</style>
